import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';

export default function AboutUsPage() {
  const navigate = useNavigate();
  usePageMeta({
    title: 'About Us - Wricoo',
    description: 'Wricoo was built to eliminate the back-and-forth chaos that content teams quietly tolerate every day.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/about/',
  });
  return (
    <div className="min-h-screen bg-white" data-testid="about-us-page">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="font-bold text-gray-900 leading-tight mb-4">About Wricoo</h1>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">Content teams are not slow. Processes are.</p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-16">
        {/* Intro */}
        <section>
          <p className="text-gray-600 leading-relaxed mb-4">In most companies, content still moves like it did ten years ago. SEO creates a brief in one Google Doc. The writer creates another document to draft. Editors comment. SEO reviews again. Project managers update sheets. Agencies track word counts manually. Deadlines shift. Energy drains.</p>
          <p className="text-gray-700 font-medium leading-relaxed">What should feel like a system feels like a relay race.</p>
        </section>

        {/* Why */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-blue-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Why Wricoo Exists</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">Wricoo was built to eliminate the back-and-forth chaos that content teams quietly tolerate every day.</p>
          <p className="text-gray-600 leading-relaxed mb-4">We asked a simple question: Why should brief creation, writing, editing, tracking, and accountability live in different tools?</p>
          <p className="text-gray-900 font-semibold mb-4">They shouldn't.</p>
          <p className="text-gray-600 leading-relaxed mb-4">Wricoo brings everything into one structured workspace. Create the SEO brief. Write the article. Review changes. Track revisions. Monitor progress. Keep every update inside the same environment.</p>
          <p className="text-gray-600 leading-relaxed">No document hopping. No version confusion. No bloated dashboards. No hidden inefficiencies.</p>
        </section>

        {/* What */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-indigo-500 rounded-full" />
            <h2 className="font-bold text-gray-900">What Wricoo Is</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">Wricoo is a collaborative workspace built specifically for content teams, SEO specialists, and editors who want structure without friction.</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-4">
            <p className="text-sm text-gray-600 leading-relaxed">It is <strong className="text-gray-900">not</strong> a publishing CMS. It does <strong className="text-gray-900">not</strong> generate AI content. It does <strong className="text-gray-900">not</strong> scrape. It does <strong className="text-gray-900">not</strong> auto-produce articles.</p>
          </div>
          <p className="text-gray-600 leading-relaxed">It is a controlled environment designed to help teams think clearly, execute precisely, and maintain accountability.</p>
        </section>

        {/* Who */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-violet-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Who Wricoo Is For</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-200 hover:shadow-sm transition-all">
              <h3 className="text-base font-semibold text-gray-900 mb-3">SaaS Companies with In-House Content Teams</h3>
              <p className="text-sm text-gray-600 leading-relaxed">When marketing, SEO, and writers work under one roof, coordination matters more than creativity. Wricoo keeps briefs, drafts, revisions, and ownership in one visible system.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-200 hover:shadow-sm transition-all">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Digital Marketing Agencies</h3>
              <p className="text-sm text-gray-600 leading-relaxed">Agencies managing multiple clients and freelancers need separation, structure, and billing clarity. Wricoo allows separate workspaces for each client so teams can maintain boundaries while tracking real word output.</p>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4">No more paying for words that were never written. No more chasing documents. No more guessing who updated what.</p>
        </section>

        {/* Accountability */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-emerald-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Designed for Accountability</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">Wricoo is intentionally minimal.</p>
          <p className="text-gray-600 leading-relaxed mb-6">It avoids feature overload because clarity wins over complexity. It avoids AI automation because thinking still matters. It avoids public publishing because focus matters.</p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Every workspace is built to answer simple operational questions:</p>
            <ul className="space-y-2">
              {['What was the brief?', 'Who wrote it?', 'What changed?', 'What is pending?', 'How much actual work was done?'].map(q => (
                <li key={q} className="flex items-center gap-2.5 text-sm text-gray-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                  {q}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-gray-600 leading-relaxed mt-4">When content operations become measurable, teams move faster without burning out.</p>
        </section>

        {/* Replace */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-orange-500 rounded-full" />
            <h2 className="font-bold text-gray-900">One Workspace. One Flow.</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">Wricoo replaces:</p>
          <div className="grid grid-cols-2 gap-3 mb-6">
            {['Scattered Google Docs', 'Manual content sheets', 'Confusing version trails', 'Spreadsheet tracking chaos'].map(item => (
              <div key={item} className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-red-700">{item}</div>
            ))}
          </div>
          <p className="text-gray-600 leading-relaxed">With a single collaborative room where strategy and execution live together. Brief. Write. Edit. Update. Track. All inside one system.</p>
        </section>

        {/* Philosophy */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-8 sm:p-10">
          <h2 className="font-bold text-gray-900 mb-4">The Philosophy</h2>
          <div className="space-y-2 text-gray-700 leading-relaxed">
            <p>Content production should feel controlled, not chaotic.</p>
            <p>Tools should support thinking, not interrupt it.</p>
            <p>Accountability should be built-in, not manually enforced.</p>
          </div>
          <p className="text-gray-600 mt-4 text-sm">Wricoo is built on that belief.</p>
        </section>

        {/* CTA */}
        <section className="text-center pb-4">
          <h2 className="font-bold text-gray-900 mb-3">Start Building Smarter</h2>
          <p className="text-gray-600 leading-relaxed mb-6">If your content workflow feels heavier than it should, you are not alone. Wricoo is designed to bring that structure back.</p>
          <Button size="lg" className="h-12 px-8" onClick={() => navigate('/register/')}>
            Start for Free <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
